import { format } from 'date-fns';
import { useState } from 'react';
import type { ChiTietPhieuXuatKho, ChiTietPhieuXuatKhoInput, PhieuXuatKho, PhieuXuatKhoInput } from '@/types/schemas';
import { AritoFormV2, transformDocumentNumber } from '@/components/custom/erp';
import { handleUpdateRowFields, useFormFieldState } from '../../hooks';
import { getFormActionButtonsWithInvoiceParams } from '../../utils';
import { usePhieuXuatKhoSearchContext } from '../../context';
import { formSchema, initialFormValues } from '../../schema';
import { BasicInformationTab } from './BasicInformationTab';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import type { DetailRow } from './hooks/useDetailRows';
import { calculateTotals } from '../../hooks/useCalc';
import { isValidUUID } from '@/lib/uuid-validator';
import { useAuth } from '@/contexts/auth-context';
import { roundNumber } from '@/lib/formatUtils';
import type { FormSchema } from '../../schema';
import InfoDropdown from '../InfoDropdown';
import { FormMode } from '@/types/form';
import { DetailTab } from './DetailTab';
import { useDetailRows } from './hooks';
import BottomBar from '../BottomBar';
import { FileTab } from './FileTab';

interface FormDialogProps {
  open?: boolean;
  formMode: FormMode;
  isCopyMode?: boolean;
  initialData?: PhieuXuatKho | null;
  onSubmit: (data: PhieuXuatKhoInput) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  initialData,
  onClose,
  onSubmit,
  formMode,
  isCopyMode,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const { state, actions } = useFormFieldState(initialData);
  const { customer, quyenChungTu, soChungTu } = state;
  const { setCustomer, setQuyenChungTu, setSoChungTu } = actions;
  const { entityUnit } = useAuth();
  const { hasInvoiceParams, transactionOptions } = usePhieuXuatKhoSearchContext();
  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setNonWritableFields: detailSetNonWritableFields,
    setPasteConfigs: detailSetPasteConfigs
  } = useDetailRows((initialData?.chi_tiet || []) as DetailRow[], undefined, handleUpdateRowFields);
  const calcTotals = calculateTotals(detailRows);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows
  });

  const normalizeSubmitData = (data: FormSchema): FormSchema => {
    const defaultMaGD =
      formMode === 'add'
        ? transactionOptions[0]?.value || initialFormValues.ma_gd || 'NB'
        : initialData?.ma_gd ||
          initialData?.ma_gd_data?.uuid ||
          transactionOptions[0]?.value ||
          initialFormValues.ma_gd ||
          'NB';

    return {
      ...initialFormValues,
      ...data,
      ma_gd: data.ma_gd || defaultMaGD
    };
  };

  const handleSubmit = (data: FormSchema) => {
    const submitData = normalizeSubmitData(data);
    const transformedDetailRows = detailRows.map(
      (row: ChiTietPhieuXuatKho, index: number): ChiTietPhieuXuatKhoInput => {
        const transformedRow: ChiTietPhieuXuatKhoInput = {
          line: index + 1,
          uuid: formMode != 'add' && isValidUUID(row.uuid) ? row.uuid : undefined,
          ma_vt: row.ma_vt_data?.uuid || null,
          dvt: typeof row.dvt === 'string' ? row.dvt : row.ma_vt_data?.dvt || null,
          ma_kho: row.ma_vt_data?.ma_kho_data?.uuid || row.ma_kho_data?.uuid || null,
          ma_bp: row.ma_bp_data?.uuid || null,
          ma_vv: row.ma_vv_data?.uuid || null,
          ma_hd: row.ma_hd_data?.uuid || null,
          ma_dtt: row.ma_dtt_data?.uuid || null,
          ma_ku: row.ma_ku_data?.uuid || null,
          ma_phi: row.ma_phi_data?.uuid || null,
          ma_sp: row.ma_sp_data?.uuid || null,
          ma_cp0: row.ma_cp0_data?.uuid || null,
          tk_vt: row.tk_vt_data?.uuid || null,
          tk_du: row.tk_du_data?.uuid || null,
          ma_nx: row.ma_nx_data?.uuid || null,
          ma_lsx: row.ma_lsx_data?.uuid || '',
          so_luong: roundNumber(row.so_luong, 6),
          gia_nt: roundNumber(row.gia_nt, 6),
          tien_nt: roundNumber(row.tien_nt, 6),
          gia: roundNumber(row.gia_nt, 6),
          tien: roundNumber(row.tien_nt, 6),
          px_dd: row.px_dd || false,
          line_yc: row.line_yc || 0
        };

        return transformedRow;
      }
    );

    const documentNumberData = transformDocumentNumber(quyenChungTu, soChungTu);

    const enhancedData: PhieuXuatKhoInput = {
      ...submitData,
      ngay_ct: format(submitData.ngay_ct || new Date(), 'yyyy-MM-dd'),
      ngay_lct: format(submitData.ngay_ct || new Date(), 'yyyy-MM-dd'),
      ma_kh: customer?.uuid || null,
      ...documentNumberData,
      ...calcTotals,
      chi_tiet: transformedDetailRows,
      t_tien: calcTotals.t_tien_nt ? calcTotals.t_tien_nt : 0,
      t_tien_nt: calcTotals.t_tien_nt ? calcTotals.t_tien_nt : 0,
      unit_id: entityUnit?.uuid || null
    };

    if (isCopyMode) {
      delete (enhancedData as Record<string, unknown>).nguon_hoa_don_id;
      delete (enhancedData as Record<string, unknown>).tu_dong_tao;
    }

    onSubmit(enhancedData);
  };

  const actionButtons = getFormActionButtonsWithInvoiceParams(hasInvoiceParams, formMode, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    onClose
  });

  return (
    <div className='h-full flex-1 lg:overflow-hidden'>
      <AritoFormV2
        key={JSON.stringify(initialData)}
        mode={formMode}
        initialData={(initialData || initialFormValues) as Partial<FormSchema>}
        title={formMode === 'add' ? 'Mới' : formMode === 'edit' ? 'Sửa' : 'Xem'}
        className='h-[100dvh] max-h-[100dvh] overflow-hidden'
        onSubmit={handleSubmit}
        schema={formSchema}
        onClose={onClose}
        actionButtons={actionButtons}
        subTitle='Phiếu xuất kho'
        headerFields={[
          {
            id: 'info',
            label: 'Thông tin',
            component: (
              <BasicInformationTab
                formMode={formMode}
                customer={customer}
                setCustomer={setCustomer}
                quyenChungTu={quyenChungTu}
                setQuyenChungTu={setQuyenChungTu}
                soChungTu={soChungTu}
                setSoChungTu={setSoChungTu}
              />
            )
          }
        ]}
        headerTabActions={
          formMode !== 'add' ? (
            <InfoDropdown uuid={initialData?.uuid || ''} ma_ct={initialData?.chung_tu || ''} />
          ) : undefined
        }
        tabs={[
          {
            id: 'details',
            label: 'Chi tiết',
            component: (
              <DetailTab
                formMode={formMode}
                rows={detailRows}
                documentNumber={soChungTu || initialData?.so_ct}
                selectedRowUuid={detailSelectedRowUuid}
                selectedCell={detailSelectedCell}
                onRowClick={detailHandleRowClick}
                onCellClick={detailHandleCellClick}
                onAddRow={detailHandleAddRow}
                onDeleteRow={detailHandleDeleteRow}
                onCopyRow={detailHandleCopyRow}
                onPasteRow={detailHandlePasteRow}
                onMoveRow={detailHandleMoveRow}
                onCellValueChange={(rowUuid, field, newValue) => {
                  if (!rowUuid) return;
                  detailHandleCellValueChange(
                    rowUuid,
                    field,
                    newValue as Parameters<typeof detailHandleCellValueChange>[2]
                  );
                }}
                handleUpdateNonWritableFields={detailSetNonWritableFields}
                handleUpdatePasteConfigs={detailSetPasteConfigs}
                onRepeatForRows={handleRepeatDetailRows}
              />
            )
          },
          {
            id: 'file',
            label: 'File',
            component: <FileTab formMode={formMode} />
          }
        ]}
        bottomBar={<BottomBar totalQuantity={calcTotals.t_so_luong} totalAmount={calcTotals.t_tien} />}
      />
    </div>
  );
};

export default FormDialog;
